import React, { Fragment, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { makeStyles, Theme } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import FilterListIcon from '@material-ui/icons/FilterList';
import { SvgIconProps } from '@material-ui/core/SvgIcon';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    borderRadius: 4,
    padding: `${theme.spacing(1) / 2}px ${theme.spacing(1)}px`,
    color: theme.palette.grey[800]
  },
  sortIcon: {
    fontSize: '24px'
  },
  selected: {
    color: 'white',
    backgroundColor: `${theme.palette.secondary.main} !important`
  },
  menuIcon: {
    marginRight: theme.spacing(2)
  },
  iconLabel: {
    color: 'black',
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5)
  },
  downIcon: {
    fontSize: '24px',
    marginLeft: -theme.spacing(0.5),
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shortest
    })
  },
  downIconRotate: {
    transform: 'rotate(180deg)',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shortest
    })
  }
}));

export interface FilterOptions {
  getValue: () => string;
  getLabel: () => string;
  Icon?: (props: SvgIconProps) => JSX.Element;
}

interface FilterControlProps {
  selectedFilter?: string;
  label?: string;
  options: FilterOptions[];
  onChange: (value: string) => void;
}

const FilterControl: React.FC<FilterControlProps> = ({
  label,
  selectedFilter,
  options,
  onChange
}) => {
  const classes = useStyles();
  const { t } = useTranslation('FilterControl');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleButtonClick: React.MouseEventHandler<HTMLButtonElement> = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreOptionsMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleChange = (value: string) => (): void => {
    setAnchorEl(null);
    onChange(value);
  };

  const filterStyle = selectedFilter || options[0].getValue();

  const renderViewOptions = (filterOptions: FilterOptions[]): React.ReactNode =>
    filterOptions.map(({ Icon, getValue, getLabel }, idx) => (
      <MenuItem
        key={idx}
        selected={filterStyle === getValue()}
        onClick={handleChange(getValue())}
        className={clsx({ [classes.selected]: filterStyle === getValue() })}
      >
        {Icon && <Icon className={classes.sortIcon} />}
        <Typography>{getLabel()}</Typography>
      </MenuItem>
    ));

  return (
    <Fragment>
      <IconButton
        aria-controls="switch-view-menu"
        aria-label={t('Switch filter options')}
        aria-haspopup="true"
        onClick={handleButtonClick}
        className={classes.button}
      >
        <FilterListIcon className={classes.sortIcon} />
        <Typography className={classes.iconLabel} align="center" variant="body1">
          {label || t('Filter')}
        </Typography>
        <KeyboardArrowDownIcon color="secondary" className={clsx(classes.downIcon)} />
      </IconButton>
      <Menu
        id="switch-view-menu"
        variant="menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMoreOptionsMenuClose}
      >
        {renderViewOptions(options)}
      </Menu>
    </Fragment>
  );
};

export default FilterControl;
